import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/StatCard";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Send, Sparkles, CheckCircle2, Tag, UserCheck, Bot, Wand2 } from "lucide-react";
import { Stat, Section, Empty, Pill, useFetch, dt } from "./_shared";

/* ======================= Support-Anfragen (support_threads) ======================= */
const THREAD_TONE = { open: "bad", escalated: "warn", resolved: "good" };
const THREAD_STATUSES = [
  { key: "escalated", label: "Eskaliert" },
  { key: "open", label: "Offen" },
  { key: "resolved", label: "Gelöst" },
  { key: "", label: "Alle" },
];

function ThreadsTab() {
  const [statusFilter, setStatusFilter] = useState("escalated");
  const [threads, setThreads] = useState([]);
  const [kpi, setKpi] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [autoResolve, setAutoResolve] = useState(false);

  const loadThreads = useCallback(async () => {
    setLoading(true);
    try {
      const qs = statusFilter ? `?status=${statusFilter}&limit=200` : "?limit=200";
      const { data } = await api.get(`/support/admin/threads${qs}`);
      setThreads(data.threads || []);
      setKpi(data.kpi || {});
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  }, [statusFilter]);
  useEffect(() => { loadThreads(); }, [loadThreads]);

  const openThread = useCallback(async (tid) => {
    setSelectedId(tid); setDetailLoading(true); setAnalysis(null); setReply("");
    try {
      const { data } = await api.get(`/support/admin/threads/${tid}`);
      setDetail(data);
    } catch (err) { toast.error(apiError(err)); } finally { setDetailLoading(false); }
  }, []);

  const [searchParams] = useSearchParams();
  const threadParam = searchParams.get("thread");
  useEffect(() => { if (threadParam) openThread(threadParam); }, [threadParam, openThread]);

  const sendReply = async (resolve) => {
    if (!reply.trim()) { toast.error("Antwort darf nicht leer sein."); return; }
    setBusy(true);
    try {
      await api.post(`/support/admin/threads/${selectedId}/reply`, { body: reply.trim(), resolve });
      toast.success(resolve ? "Gesendet & gelöst." : "Antwort gesendet.");
      await openThread(selectedId); await loadThreads();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const resolveThread = async () => {
    setBusy(true);
    try { await api.post(`/support/admin/threads/${selectedId}/resolve`); toast.success("Als gelöst markiert."); await openThread(selectedId); await loadThreads(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const runAi = async () => {
    setAiLoading(true); setAnalysis(null);
    try {
      const { data } = await api.post(`/support/admin/threads/${selectedId}/ai-analysis`, { auto_resolve: autoResolve });
      setAnalysis(data.analysis);
      if (data.error) toast.message("KI-Fallback", { description: "KI derzeit eingeschränkt verfügbar." });
      if (data.auto_resolved) { toast.success("KI hat geantwortet & den Fall gelöst."); await openThread(selectedId); await loadThreads(); }
    } catch (err) { toast.error(apiError(err)); } finally { setAiLoading(false); }
  };

  const byStatus = kpi.by_status || {};

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Eskaliert" value={byStatus.escalated ?? 0} tone={byStatus.escalated ? "warn" : "good"} testId="admin-threads-escalated" />
        <Stat label="Offen" value={byStatus.open ?? 0} tone={byStatus.open ? "bad" : "good"} />
        <Stat label="Gelöst" value={byStatus.resolved ?? 0} tone="good" />
        <Stat label="Gesamt" value={kpi.total ?? 0} />
      </div>

      <div className="flex flex-wrap gap-1.5" data-testid="admin-threads-filters">
        {THREAD_STATUSES.map((s) => (
          <button key={s.key || "all"} onClick={() => setStatusFilter(s.key)} data-testid={`admin-threads-filter-${s.key || "all"}`}
            className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${statusFilter === s.key ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
            {s.label}
          </button>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,360px)_1fr]">
        <Section title={`Anfragen (${threads.length})`} testId="admin-threads-list">
          {loading ? <Skeleton className="h-64 rounded-2xl" /> : threads.length === 0 ? <Empty text="Keine Anfragen." /> : (
            <div className="space-y-1.5">
              {threads.map((t) => (
                <button key={t.id} onClick={() => openThread(t.id)} data-testid={`admin-thread-${t.id}`}
                  className={`w-full rounded-xl border p-3 text-left transition-colors ${selectedId === t.id ? "border-primary bg-primary/5" : "border-border bg-background hover:bg-accent"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm font-medium text-foreground">{t.subject || "Anfrage"}</span>
                    <Pill tone={THREAD_TONE[t.status] || "default"}>{t.status}</Pill>
                  </div>
                  <p className="mt-0.5 truncate text-xs text-muted-foreground">{t.user_name} · {t.category} · {dt(t.updated_at)}</p>
                </button>
              ))}
            </div>
          )}
        </Section>

        <Section title="Fall-Detail" testId="admin-thread-detail">
          {!selectedId ? <Empty text="Wähle links eine Anfrage." /> : detailLoading ? <Skeleton className="h-64 rounded-2xl" /> : detail ? (
            <div className="space-y-4">
              <div className="rounded-xl border border-border bg-background p-3">
                <p className="text-sm font-semibold text-foreground">{detail.thread.subject || "—"} <Pill tone={THREAD_TONE[detail.thread.status] || "default"}>{detail.thread.status}</Pill></p>
                <p className="text-xs text-muted-foreground">{detail.thread.user_name} · {detail.thread.user_email || "keine E-Mail"} · {detail.thread.category}</p>
              </div>

              <div className="max-h-72 space-y-2 overflow-auto" data-testid="admin-thread-messages">
                {(detail.messages || []).map((m) => (
                  <div key={m.id} className={`rounded-xl p-3 text-sm ${m.role === "human" || m.role === "admin" ? "ml-6 bg-primary/10" : m.role === "ai" ? "mr-6 bg-sky-500/10" : m.role === "system" ? "mx-2 bg-muted" : "mr-6 bg-secondary"}`}>
                    <p className="mb-0.5 text-[11px] font-semibold text-muted-foreground">{m.admin_name || m.role} · {dt(m.created_at)}</p>
                    <p className="whitespace-pre-wrap text-foreground">{m.text}</p>
                  </div>
                ))}
              </div>

              {/* KI-Schnellanalyse */}
              <div className="rounded-xl border border-primary/30 bg-primary/5 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="flex items-center gap-1.5 text-xs font-semibold text-primary"><Bot className="h-4 w-4" /> KI-Analyse</p>
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-1.5 text-xs text-muted-foreground" title="KI beantwortet & löst den Fall automatisch">
                      <input type="checkbox" checked={autoResolve} onChange={(e) => setAutoResolve(e.target.checked)} className="h-4 w-4 accent-primary" data-testid="admin-thread-autoresolve" />
                      Auto-Lösung
                    </label>
                    <Button size="sm" className="h-8 gap-1.5 rounded-full" disabled={aiLoading} onClick={runAi} data-testid="admin-thread-ai">
                      <Sparkles className="h-3.5 w-3.5" /> {aiLoading ? "Analysiert …" : "Analysieren"}
                    </Button>
                  </div>
                </div>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Die KI fasst zusammen, was passiert ist, schlägt einen Lösungsweg vor und erstellt einen Antwort-Entwurf.
                  Mit „Auto-Lösung&ldquo; sendet die KI den Entwurf direkt an den Nutzer und markiert den Fall als gelöst.
                </p>
                {analysis && (
                  <div className="mt-3 space-y-2 text-sm" data-testid="admin-thread-analysis">
                    {analysis.summary && <p><span className="font-semibold">Was ist passiert:</span> {analysis.summary}</p>}
                    {Array.isArray(analysis.solution_path) && analysis.solution_path.length > 0 && (
                      <div>
                        <p className="font-semibold">Lösungsweg:</p>
                        <ol className="ml-4 list-decimal space-y-0.5 text-muted-foreground">
                          {analysis.solution_path.map((s, i) => <li key={i}>{s}</li>)}
                        </ol>
                      </div>
                    )}
                    {analysis.draft_reply && (
                      <div className="rounded-lg border border-border bg-background p-2">
                        <p className="mb-1 text-[11px] font-semibold text-muted-foreground">Antwort-Entwurf</p>
                        <p className="whitespace-pre-wrap text-foreground">{analysis.draft_reply}</p>
                        <Button size="sm" variant="outline" className="mt-2 h-7 gap-1.5 rounded-full text-xs" onClick={() => setReply(analysis.draft_reply)} data-testid="admin-thread-use-draft">
                          <Wand2 className="h-3.5 w-3.5" /> In Antwort übernehmen
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Antwort */}
              <div>
                <span className="mb-1.5 block text-xs font-semibold text-muted-foreground">Antwort an den Nutzer</span>
                <Textarea value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Antwort schreiben oder KI-Entwurf übernehmen…" className="min-h-[110px]" data-testid="admin-thread-reply" />
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button size="sm" className="gap-1.5 rounded-full" disabled={busy} onClick={() => sendReply(false)} data-testid="admin-thread-send"><Send className="h-4 w-4" /> Senden</Button>
                  <Button size="sm" variant="outline" className="gap-1.5 rounded-full" disabled={busy} onClick={() => sendReply(true)} data-testid="admin-thread-send-resolve"><Send className="h-4 w-4" /> Senden & lösen</Button>
                  {detail.thread.status !== "resolved" && <Button size="sm" variant="outline" className="gap-1.5 rounded-full text-emerald-600" disabled={busy} onClick={resolveThread} data-testid="admin-thread-resolve"><CheckCircle2 className="h-4 w-4" /> Als gelöst markieren</Button>}
                </div>
              </div>
            </div>
          ) : <Empty text="Konnte Anfrage nicht laden." />}
        </Section>
      </div>
    </div>
  );
}

/* ======================= Coach-Tickets (coach_support_tickets) ======================= */
const STATUS_TONE = { open: "bad", in_progress: "warn", answered: "info", closed: "default" };
const STATUSES = [
  { key: "", label: "Alle" },
  { key: "open", label: "Offen" },
  { key: "in_progress", label: "In Arbeit" },
  { key: "answered", label: "Beantwortet" },
  { key: "closed", label: "Geschlossen" },
];
const PRIORITIES = ["urgent", "high", "normal", "low"];

function CoachTicketsTab() {
  const { data: constants } = useFetch("/support/constants");
  const { data: adminsData } = useFetch("/admin/support/admins");
  const [statusFilter, setStatusFilter] = useState("open");
  const [tickets, setTickets] = useState([]);
  const [kpi, setKpi] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [reply, setReply] = useState("");
  const [cats, setCats] = useState([]);
  const [priority, setPriority] = useState("normal");
  const [busy, setBusy] = useState(false);

  const adminCats = constants?.admin_categories || [];
  const admins = adminsData?.admins || [];

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const qs = statusFilter ? `?status=${statusFilter}&limit=100` : "?limit=100";
      const { data } = await api.get(`/admin/support/tickets${qs}`);
      setTickets(data.tickets || []);
      setKpi(data.kpi || {});
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  }, [statusFilter]);
  useEffect(() => { loadTickets(); }, [loadTickets]);

  const openTicket = useCallback(async (tid) => {
    setSelectedId(tid);
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/admin/support/tickets/${tid}`);
      setDetail(data);
      setCats(data.ticket?.admin_categories || []);
      setPriority(data.ticket?.priority || "normal");
      setReply("");
    } catch (err) { toast.error(apiError(err)); } finally { setDetailLoading(false); }
  }, []);

  const toggleCat = (k) => setCats((c) => c.includes(k) ? c.filter((x) => x !== k) : [...c, k]);

  const saveCats = async () => {
    if (cats.length === 0) { toast.error("Mindestens eine Kategorie wählen."); return; }
    setBusy(true);
    try { await api.post(`/admin/support/tickets/${selectedId}/categorize`, { admin_categories: cats, priority }); toast.success("Kategorisiert."); await openTicket(selectedId); await loadTickets(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const aiDraft = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/admin/support/tickets/${selectedId}/ai-draft`, {}); setReply(data.draft || ""); toast.success("KI-Entwurf eingefügt."); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const sendReply = async (closeAfter) => {
    if (!reply.trim()) { toast.error("Antwort darf nicht leer sein."); return; }
    setBusy(true);
    try { await api.post(`/admin/support/tickets/${selectedId}/messages`, { body: reply.trim(), close_after: closeAfter }); toast.success(closeAfter ? "Gesendet & geschlossen." : "Antwort gesendet."); await openTicket(selectedId); await loadTickets(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const assign = async (adminId) => {
    if (!adminId) return;
    setBusy(true);
    try { await api.post(`/admin/support/tickets/${selectedId}/assign`, { admin_id: adminId }); toast.success("Zugewiesen."); await openTicket(selectedId); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const closeTicket = async () => {
    setBusy(true);
    try { await api.post(`/admin/support/tickets/${selectedId}/close`); toast.success("Geschlossen."); await openTicket(selectedId); await loadTickets(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const byStatus = kpi.by_status || {};

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Offen" value={byStatus.open ?? 0} tone={byStatus.open ? "bad" : "good"} testId="admin-support-open" />
        <Stat label="In Arbeit" value={byStatus.in_progress ?? 0} tone={byStatus.in_progress ? "warn" : "default"} />
        <Stat label="Beantwortet" value={byStatus.answered ?? 0} />
        <Stat label="Gesamt" value={kpi.total ?? 0} />
      </div>

      <div className="flex flex-wrap gap-1.5" data-testid="admin-support-filters">
        {STATUSES.map((s) => (
          <button key={s.key || "all"} onClick={() => setStatusFilter(s.key)} data-testid={`admin-support-filter-${s.key || "all"}`}
            className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${statusFilter === s.key ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
            {s.label}
          </button>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,360px)_1fr]">
        <Section title={`Tickets (${tickets.length})`} testId="admin-support-list">
          {loading ? <Skeleton className="h-64 rounded-2xl" /> : tickets.length === 0 ? <Empty text="Keine Tickets." /> : (
            <div className="space-y-1.5">
              {tickets.map((t) => (
                <button key={t.id} onClick={() => openTicket(t.id)} data-testid={`admin-support-ticket-${t.id}`}
                  className={`w-full rounded-xl border p-3 text-left transition-colors ${selectedId === t.id ? "border-primary bg-primary/5" : "border-border bg-background hover:bg-accent"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm font-medium text-foreground">{t.subject || t.coach_name || "Ticket"}</span>
                    <Pill tone={STATUS_TONE[t.status] || "default"}>{t.status}</Pill>
                  </div>
                  <p className="mt-0.5 truncate text-xs text-muted-foreground">{t.coach_name} · {t.coach_group || "—"} · {t.priority}{t.unread_by_admin ? ` · ${t.unread_by_admin} neu` : ""}</p>
                </button>
              ))}
            </div>
          )}
        </Section>

        <Section title="Ticket-Detail" testId="admin-support-detail">
          {!selectedId ? <Empty text="Wähle links ein Ticket." /> : detailLoading ? <Skeleton className="h-64 rounded-2xl" /> : detail ? (
            <div className="space-y-4">
              <div className="rounded-xl border border-border bg-background p-3">
                <p className="text-sm font-semibold text-foreground">{detail.ticket.subject || "—"} <Pill tone={STATUS_TONE[detail.ticket.status] || "default"}>{detail.ticket.status}</Pill></p>
                <p className="text-xs text-muted-foreground">{detail.ticket.coach_name} · {detail.ticket.coach_email}</p>
                <div className="mt-2 flex items-center gap-2">
                  <UserCheck className="h-4 w-4 text-muted-foreground" />
                  <select onChange={(e) => assign(e.target.value)} value={detail.ticket.assigned_admin_id || ""} className="h-8 rounded-lg border border-border bg-background px-2 text-xs text-foreground" data-testid="admin-support-assign">
                    <option value="">Nicht zugewiesen</option>
                    {admins.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
              </div>

              <div className="max-h-80 space-y-2 overflow-auto" data-testid="admin-support-messages">
                {(detail.messages || []).filter((m) => m.sender_role !== "ai_draft").map((m) => (
                  <div key={m.id} className={`rounded-xl p-3 text-sm ${m.sender_role === "admin" ? "ml-6 bg-primary/10" : "mr-6 bg-secondary"}`}>
                    <p className="mb-0.5 text-[11px] font-semibold text-muted-foreground">{m.sender_name} · {dt(m.created_at)}</p>
                    <p className="whitespace-pre-wrap text-foreground">{m.body}</p>
                  </div>
                ))}
              </div>

              <div className="rounded-xl border border-border bg-background p-3">
                <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground"><Tag className="h-3.5 w-3.5" /> Admin-Kategorien</p>
                <div className="flex flex-wrap gap-1.5">
                  {adminCats.map((c) => (
                    <button key={c.key} onClick={() => toggleCat(c.key)} data-testid={`admin-support-cat-${c.key}`}
                      className={`rounded-full px-2.5 py-1 text-xs transition-colors ${cats.includes(c.key) ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>
                      {c.de}
                    </button>
                  ))}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <select value={priority} onChange={(e) => setPriority(e.target.value)} className="h-8 rounded-lg border border-border bg-background px-2 text-xs text-foreground" data-testid="admin-support-priority">
                    {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <Button size="sm" variant="outline" className="rounded-full" disabled={busy} onClick={saveCats} data-testid="admin-support-save-cats">Speichern</Button>
                </div>
              </div>

              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <span className="text-xs font-semibold text-muted-foreground">Antwort</span>
                  <Button size="sm" variant="ghost" className="h-7 gap-1.5 rounded-full" disabled={busy} onClick={aiDraft} data-testid="admin-support-aidraft"><Sparkles className="h-3.5 w-3.5" /> KI-Entwurf</Button>
                </div>
                <Textarea value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Antwort an den Coach…" className="min-h-[120px]" data-testid="admin-support-reply" />
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button size="sm" className="gap-1.5 rounded-full" disabled={busy} onClick={() => sendReply(false)} data-testid="admin-support-send"><Send className="h-4 w-4" /> Senden</Button>
                  <Button size="sm" variant="outline" className="gap-1.5 rounded-full" disabled={busy} onClick={() => sendReply(true)} data-testid="admin-support-send-close"><Send className="h-4 w-4" /> Senden & schließen</Button>
                  {detail.ticket.status !== "closed" && <Button size="sm" variant="outline" className="gap-1.5 rounded-full text-rose-600" disabled={busy} onClick={closeTicket} data-testid="admin-support-close"><CheckCircle2 className="h-4 w-4" /> Schließen</Button>}
                </div>
              </div>
            </div>
          ) : <Empty text="Konnte Ticket nicht laden." />}
        </Section>
      </div>
    </div>
  );
}

export default function Support() {
  return (
    <div className="space-y-5">
      <PageHeader eyebrow="Admin" title="Support" subtitle="Alle Support-Fälle direkt im Dashboard sehen, beantworten & lösen — mit KI-Analyse." />
      <Tabs defaultValue="threads">
        <TabsList data-testid="admin-support-tabs">
          <TabsTrigger value="threads" data-testid="admin-support-tab-threads">Anfragen</TabsTrigger>
          <TabsTrigger value="coach" data-testid="admin-support-tab-coach">Coach-Tickets</TabsTrigger>
        </TabsList>
        <TabsContent value="threads"><ThreadsTab /></TabsContent>
        <TabsContent value="coach"><CoachTicketsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
